import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../services/api';

const getDniKey = (item) => item.competitor_dni || item.buyer_dni;

export default function EventRanking() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ranking, setRanking] = useState([]);
    const [winners, setWinners] = useState([]);
    const [draw, setDraw] = useState(null);
    const [selectedDnis, setSelectedDnis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawing, setDrawing] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const fetchRanking = useCallback(async () => {
        try {
            setError('');
            const res = await api.get(`/admin/events/${id}/ranking`);
            const nextRanking = res.data.ranking || [];
            setRanking(nextRanking);
            setWinners(res.data.winners || []);
            setDraw(res.data.draw || null);
            setSelectedDnis((prev) =>
                prev.filter((dni) => nextRanking.some((item) => getDniKey(item) === dni))
            );
        } catch (err) {
            setError(getApiErrorMessage(err, 'Error al cargar ranking'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchRanking();
    }, [fetchRanking]);

    const allSelected = ranking.length > 0 && ranking.every((item) => selectedDnis.includes(getDniKey(item)));
    const drawWinner = draw?.winner_dni;
    const drawParticipants = draw?.participants || [];

    const toggleDni = (dni) => {
        setSelectedDnis((prev) =>
            prev.includes(dni) ? prev.filter((item) => item !== dni) : [...prev, dni]
        );
    };

    const toggleAll = () => {
        setSelectedDnis(allSelected ? [] : ranking.map((item) => getDniKey(item)));
    };

    const performDraw = async (force = false) => {
        setDrawing(true);
        setError('');
        setNotice('');

        try {
            const res = await api.post(`/admin/events/${id}/ranking/draw`, {
                competitor_dnis: selectedDnis,
                ...(force ? { force: true } : {}),
            });

            setDraw(res.data.draw);
            setSelectedDnis([]);
            setNotice(
                `Sorteo realizado. Ganador: ${res.data.draw.winner_dni} (${res.data.draw.winner_points} puntos).`
            );
        } catch (err) {
            if (err.response?.status === 409) {
                const currentDraw = err.response.data?.draw;
                if (currentDraw) {
                    setDraw(currentDraw);
                }

                if (force) {
                    setError(getApiErrorMessage(err, 'Ya existe un sorteo para este evento.'));
                    return;
                }

                const winnerLabel = currentDraw?.winner_dni
                    ? ` Ganador actual: ${currentDraw.winner_dni}.`
                    : '';

                if (window.confirm(`Ya existe un sorteo para este evento.${winnerLabel} ¿Querés realizar un nuevo sorteo con los competidores seleccionados?`)) {
                    setDrawing(false);
                    await performDraw(true);
                    return;
                }

                setNotice('Se mantiene el sorteo anterior.');
                return;
            }

            const validationMessage = err.response?.data?.errors?.competitor_dnis?.[0];
            setError(validationMessage || getApiErrorMessage(err, 'Error al realizar el sorteo'));
        } finally {
            setDrawing(false);
        }
    };

    const handleDrawClick = () => {
        if (selectedDnis.length < 2) {
            setError('Seleccioná al menos 2 competidores para realizar el sorteo.');
            return;
        }

        if (draw && !window.confirm(`Ya existe un sorteo para este evento. Ganador actual: ${draw.winner_dni}. ¿Querés reemplazarlo?`)) {
            return;
        }

        performDraw(Boolean(draw));
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Ranking de Competidores</h2>
                    <p className="text-sm text-gray-500">Seleccioná competidores del ranking para realizar el sorteo.</p>
                </div>
                <button onClick={() => navigate('/admin/dashboard')} className="px-3 py-1 bg-gray-200 rounded">Volver</button>
            </div>

            {error && <div className="p-4 bg-red-100 text-red-700 rounded">{error}</div>}
            {notice && <div className="p-4 bg-green-100 text-green-700 rounded">{notice}</div>}
            {loading ? (
                <div className="text-center py-10 text-gray-500">Cargando...</div>
            ) : (
                <>
                    {draw && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1">
                            <h3 className="text-lg font-bold text-emerald-800">Último sorteo</h3>
                            <p className="text-emerald-800">
                                Ganador: <span className="font-bold">{draw.winner_dni}</span> ({draw.winner_points} puntos)
                            </p>
                            <p className="text-sm text-emerald-700">
                                Participantes ({drawParticipants.length}): {drawParticipants.map((item) => item.competitor_dni || item).join(', ')}
                            </p>
                            {draw.drawn_at && (
                                <p className="text-xs text-emerald-600">
                                    Realizado: {new Date(draw.drawn_at).toLocaleString('es-AR')}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">{selectedDnis.length} seleccionados</p>
                                {selectedDnis.length < 2 && (
                                    <p className="text-xs text-gray-500">Seleccioná al menos 2 competidores para sortear.</p>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={toggleAll}
                                    disabled={ranking.length === 0 || drawing}
                                    className="px-3 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {allSelected ? 'Limpiar selección' : 'Seleccionar todos'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDrawClick}
                                    disabled={selectedDnis.length < 2 || drawing}
                                    className="px-3 py-2 text-sm font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                >
                                    {drawing ? 'Sorteando...' : draw ? 'Volver a sortear' : 'Sortear seleccionados'}
                                </button>
                            </div>
                        </div>

                        {winners.length > 0 && (
                            <div className="p-4 bg-amber-50 border-b border-amber-200 text-center">
                                <p className="text-amber-700 font-bold">Ganador(es) del ranking: {winners.join(', ')}</p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="w-12 px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            disabled={ranking.length === 0 || drawing}
                                            aria-label="Seleccionar todos los competidores"
                                        />
                                    </th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Posicion</th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Nombre comprador</th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">DNI comprador</th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">DNI competidor</th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase text-right">Puntos</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {ranking.map((item) => {
                                    const dniKey = getDniKey(item);
                                    const isSelected = selectedDnis.includes(dniKey);
                                    const isDrawWinner = drawWinner === dniKey;
                                    const isRankingWinner = winners.includes(item.buyer_dni) || winners.includes(item.competitor_dni);
                                    const rowClass = isDrawWinner
                                        ? 'bg-emerald-50'
                                        : isSelected
                                            ? 'bg-indigo-50'
                                            : isRankingWinner
                                                ? 'bg-amber-50'
                                                : '';

                                    return (
                                        <tr key={dniKey} className={rowClass}>
                                            <td className="px-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleDni(dniKey)}
                                                    disabled={drawing}
                                                    aria-label={`Seleccionar ${dniKey}`}
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">{item.position}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span>{item.buyer_name || '-'}</span>
                                                    {isDrawWinner && (
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                                                            Ganador sorteo
                                                        </span>
                                                    )}
                                                    {isRankingWinner && (
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                                            Ganador ranking
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{item.buyer_dni || '-'}</td>
                                            <td className="px-6 py-4 text-sm font-mono text-gray-600">{item.competitor_dni || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold">{item.points}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                        {ranking.length === 0 && <div className="p-6 text-center text-gray-500">Sin datos</div>}
                    </div>
                </>
            )}
        </div>
    );
}
